import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageModule } from 'primeng/message';

export interface LearnMore {
  id?: string;
  coverImage?: string;
  title: string;
  description: string;
  linkUrl: string;
}

@Component({
  selector: 'app-learn-more-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    FileUploadModule,
    MessageModule
  ],
  templateUrl: './learn-more-dialog.component.html',
  styleUrls: ['./learn-more-dialog.component.css']
})
export class LearnMoreDialogComponent implements OnInit, OnDestroy {
  isEdit: boolean = false;
  previewImageUrl: string | null = null;
  submitting = signal(false);
  
  private submitTimeout?: number;

  // Form controls
  titleControl = new FormControl('', [Validators.required, Validators.minLength(3)]);
  descriptionControl = new FormControl('', [Validators.required, Validators.minLength(10)]);
  linkUrlControl = new FormControl('', [Validators.required, this.urlValidator]);
  coverImageControl = new FormControl('');

  learnMoreForm: FormGroup;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private translateService: TranslateService
  ) {
    this.learnMoreForm = new FormGroup({
      title: this.titleControl,
      description: this.descriptionControl,
      linkUrl: this.linkUrlControl,
      coverImage: this.coverImageControl
    });
  }

  ngOnInit(): void {
    // Check if we're editing an existing learn more item
    if (this.config.data?.learnMore) {
      this.isEdit = true;
      const item: LearnMore = this.config.data.learnMore;
      
      this.titleControl.setValue(item.title);
      this.descriptionControl.setValue(item.description);
      this.linkUrlControl.setValue(item.linkUrl);
      
      if (item.coverImage) {
        this.previewImageUrl = item.coverImage;
        this.coverImageControl.setValue(item.coverImage);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
    }
  }

  onSubmit(): void {
    if (this.learnMoreForm.valid && !this.submitting()) {
      this.submitting.set(true);
      
      const itemData: LearnMore = {
        title: this.titleControl.value!,
        description: this.descriptionControl.value!,
        linkUrl: this.linkUrlControl.value!,
        coverImage: this.coverImageControl.value || undefined
      };

      if (this.isEdit && this.config.data?.learnMore?.id) {
        itemData.id = this.config.data.learnMore.id;
      }

      // Simulate save operation with loading state
      this.submitTimeout = window.setTimeout(() => {
        this.submitting.set(false);
        // Return the data with success flag and operation type
        this.ref.close({ 
          data: itemData, 
          success: true, 
          isEdit: this.isEdit 
        });
      }, 2000); // 2 second loading simulation
    } else if (!this.learnMoreForm.valid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.learnMoreForm.controls).forEach(key => {
        this.learnMoreForm.get(key)?.markAsTouched();
      });
    }
  }

  onCancel(): void {
    this.ref.close();
  }

  onImageSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImageUrl = e.target?.result as string;
        this.coverImageControl.setValue(this.previewImageUrl);
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage(): void {
    this.previewImageUrl = null;
    this.coverImageControl.setValue('');
  }

  // URL validator
  urlValidator(control: any) {
    if (!control.value) return null;
    const urlPattern = /^https?:\/\/.+\..+/;
    return urlPattern.test(control.value) ? null : { invalidUrl: true };
  }

  // Error message methods
  getTitleErrorMessage(): string {
    if (this.titleControl.errors?.['required']) {
      return this.translateService.instant('learnMore.errors.titleRequired');
    }
    if (this.titleControl.errors?.['minlength']) {
      return this.translateService.instant('learnMore.errors.titleMinLength');
    }
    return '';
  }

  getDescriptionErrorMessage(): string {
    if (this.descriptionControl.errors?.['required']) {
      return this.translateService.instant('learnMore.errors.descriptionRequired');
    }
    if (this.descriptionControl.errors?.['minlength']) {
      return this.translateService.instant('learnMore.errors.descriptionMinLength');
    }
    return '';
  }

  getLinkUrlErrorMessage(): string {
    if (this.linkUrlControl.errors?.['required']) {
      return this.translateService.instant('learnMore.errors.linkUrlRequired');
    }
    if (this.linkUrlControl.errors?.['invalidUrl']) {
      return this.translateService.instant('learnMore.errors.linkUrlInvalid');
    }
    return '';
  }
}